import { useState } from 'react';
import {
  Button, DialogActions, DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { updateCompany } from './api';
import { apiErrorMessage } from '@/lib/errors';
import type { Company } from './types';
import { GlassDialog } from '@/components/GlassDialog';

export function EditCompanyDialog({
  company, open, onClose, onSaved, basePath = '/api/companies',
}: { company: Company | null; open: boolean; onClose: () => void; onSaved: () => void; basePath?: string }) {
  const [taxCode, setTaxCode] = useState('');
  const [approveNo, setApproveNo] = useState('');
  const [approveDate, setApproveDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-initialize fields each time the dialog OPENS (not merely when the company
  // id changes), so edits abandoned via Cancel don't resurface on reopen.
  const [wasOpen, setWasOpen] = useState(false);
  if (open && !wasOpen) {
    setWasOpen(true);
    setTaxCode(company?.taxCode ?? '');
    setApproveNo(company?.approveNo ?? '');
    setApproveDate(company?.approveDate ? parseISO(company.approveDate) : null);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  async function save() {
    if (!company) return;
    setSaving(true);
    try {
      await toast.promise(
        updateCompany(company.id, {
          taxCode: taxCode.trim() || null,
          approveNo: approveNo.trim() || null,
          approveDate: approveDate ? format(approveDate, 'yyyy-MM-dd') : null,
        }, basePath),
        { loading: 'ກຳລັງບັນທຶກ...', success: 'ບັນທຶກສຳເລັດ', error: (e) => apiErrorMessage(e, 'ບັນທຶກບໍ່ສຳເລັດ') },
      );
      onSaved();
      onClose();
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassDialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>ແກ້ໄຂ ພາສີ / ການອະນຸມັດ</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="TIN (taxCode)" value={taxCode} onChange={(e) => setTaxCode(e.target.value)} fullWidth />
          <TextField label="ເລກອະນຸມັດ (approveNO)" value={approveNo} onChange={(e) => setApproveNo(e.target.value)} fullWidth />
          <DatePicker label="ວັນທີອະນຸມັດ" value={approveDate} onChange={(v) => setApproveDate(v)} format="dd/MM/yyyy" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">ຍົກເລີກ</Button>
        <Button onClick={save} variant="contained" loading={saving}>ບັນທຶກ</Button>
      </DialogActions>
    </GlassDialog>
  );
}
